import { useEffect, useState } from "react";
import { Locataire } from "../../model/Model";
import { ajouterLocataire, getLocataires, modifierLocataire, supprimerLocataire } from "../../service/locataires/locataires.service";
import { AjouterLocatairePopup } from "./AjouterLocataire";

function Locataires(){
    const [locataires, setLocataires] = useState<Locataire[]>([]);
    const [seen, setSeen] = useState(false);
    const [locataireId, setLocataireId] = useState<number | undefined>(undefined);
    const [menuOpen, setMenuOpen] = useState<number | null>(null);

    useEffect(() => {
        async function fetchData() {
            const data = await getLocataires();
            console.log(data);
            setLocataires(data);
        }
        fetchData();
    }, []);

    // Fermer le menu quand on clique en dehors
    useEffect(() => {
        const handleClickOutside = () => {
            if (menuOpen !== null) {
                setMenuOpen(null);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [menuOpen]);

    function togglePopup() {
        setSeen(!seen);
        setLocataireId(undefined);
    }

    const closePopup = () => {
        setSeen(false);
        setLocataireId(undefined);
    };

    async function ajouterModifier(locataire: Locataire): Promise<void> {
        try {
            if (locataire.locataireId) {
                await modifierLocataire(locataire);
            } else {
                await ajouterLocataire(locataire);
            }
        } catch (error) {
            console.log(error);
        } finally {
            const locataires = await getLocataires();
            setLocataires(locataires);
            setSeen(false);
        }
    }

    async function handleSupprimer(id: number | undefined) {
        if (id === undefined) return;
        try {
            await supprimerLocataire(id);
            const locataires = await getLocataires();
            setLocataires(locataires);
        } catch (error) {
            console.log(error);
        }
    }

    function handleModifier(id: number | undefined) {
        setLocataireId(id);
        setSeen(true);
    }

    const toggleMenu = (id: number | undefined) => {
        if (id !== undefined) {
            setMenuOpen(menuOpen === id ? null : id);
        }
    };

    const handleMenuClick = (e: React.MouseEvent, id: number | undefined) => {
        e.stopPropagation();
        toggleMenu(id);
    };

    return(
        <div className="pt-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <button 
                        onClick={togglePopup}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                    >
                        + Ajouter un locataire
                    </button>
                    {seen ? <AjouterLocatairePopup locataireId={locataireId} ajouterModifier={ajouterModifier} onClose={closePopup}/> : null}
                </div>

                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800">Liste des locataires</h2>
                    </div>
                    
                    {locataires?.length > 0 ? (
                        <div className="overflow-x-auto pb-20">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nom
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Prénom
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Téléphone
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Adresse
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {locataires?.map((locataire, index) => (
                                        <tr key={locataire.locataireId || index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {locataire.locataireId || index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                {locataire.nom || <span className="text-gray-400 italic">Non renseigné</span>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {locataire.prenom || <span className="text-gray-400 italic">Non renseigné</span>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {locataire.telephone ? (
                                                    <a href={`tel:${locataire.telephone}`} className="text-blue-600 hover:text-blue-800">
                                                        {locataire.telephone}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400 italic">Non renseigné</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {locataire.email ? (
                                                    <a href={`mailto:${locataire.email}`} className="text-blue-600 hover:text-blue-800">
                                                        {locataire.email}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400 italic">Non renseigné</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {locataire.adresse || <span className="text-gray-400 italic">Non renseigné</span>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="relative inline-block text-left">
                                                    <button 
                                                        onClick={(e) => handleMenuClick(e, locataire.locataireId ?? undefined)}
                                                        className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-colors"
                                                        title="Actions"
                                                    >
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                                        </svg>
                                                    </button>
                                                    
                                                    {menuOpen === (locataire.locataireId ?? undefined) && (
                                                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-100">
                                                            <div className="py-1">
                                                                <button
                                                                    className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors flex items-center"
                                                                    onClick={() => {
                                                                        setMenuOpen(null);
                                                                        handleModifier(locataire.locataireId ?? undefined);
                                                                    }}
                                                                >
                                                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                    Modifier
                                                                </button>
                                                                <button
                                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                                                                    onClick={() => {
                                                                        setMenuOpen(null);
                                                                        handleSupprimer(locataire.locataireId ?? undefined);
                                                                    }}
                                                                >
                                                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                    Supprimer
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="px-6 py-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun locataire trouvé</h3>
                            <p className="text-gray-500">Cliquez sur "Ajouter un locataire" pour commencer.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
export default Locataires;